/**
 * AI reply suggestions — how this works (no storage required)
 * ============================================================
 *
 * **Purpose**
 *   Produces short, agent-ready reply *candidates* for the current chat composer.
 *   The UI only shows them until the agent picks one or dismisses them — nothing
 *   is written to a database *by this module* for the suggestion step itself.
 *
 * **Data flow (production with an LLM)**
 *
 *   1. **Inputs (ephemeral, in memory only)**
 *      - Recent user/agent/bot messages (trimmed, last N lines).
 *      - Optional: current draft in the composer, ticket subject/summary.
 *   2. **Client** (`suggestAgentReplies` in `src/api/writingAssistantApi.js`)
 *      - Live inbox: POST `/api/v1/agent/inbox/writing-assistant` (OpenAI on human-agent-service).
 *      - Optional override: `VITE_AI_WRITING_URL` + `VITE_AI_WRITING_API_KEY`.
 *   3. **Backend (your responsibility)**
 *      - Receives the body; **does not need to store** the conversation. Typical
 *        options: (a) forward `context` + system prompt to an LLM API, (b) call
 *        a hosted “agent assist” product, (c) run a small on-prem model.
 *      - Returns JSON: `{ "suggestions": string[] }` (2–4 short strings).
 *   4. **Response handling**
 *      - The dashboard parses `suggestions` and shows them as chips. If the
 *        remote call fails, the local fallback (`generateLocalSuggestReplies`)
 *        runs so the product still works in dev.
 *
 * **Why nothing is “stored”**
 *   Suggestions are derived on demand; you only persist if *you* choose to log
 *   at the gateway/LLM layer for analytics or compliance — not in this file.
 *
 * **Privacy / security**
 *   Treat `context` as potentially PII. Use HTTPS, tenant-scoped endpoints, and
 *   avoid echoing full transcripts into untrusted third parties without a DPA.
 *
 * @see `src/api/writingAssistantApi.js` — `suggestAgentReplies`, `buildConversationContextForAi`
 */

/**
 * @param {Array<{ sender?: string, text?: string }> | null | undefined} messages
 * @returns {string}
 */
function lastUserSnippet(messages) {
  const rev = [...(messages ?? [])].reverse();
  const u = rev.find((m) => m?.sender === "user" && m?.text);
  return (u?.text ?? "").trim().slice(0, 200);
}

/**
 * @param {string} lower
 * @param {string} ticket
 * @returns {string}
 */
function selectedTicketClosing(lower, ticket) {
  if (ticket) {
    return `Regarding ticket “${ticket.slice(0, 60)}${ticket.length > 60 ? "…" : ""}”: I’ll update you here with the outcome and any follow-ups we need from you.`;
  }
  if (lower.includes("urgent") || lower.includes("asap")) {
    return `I understand this is time-sensitive. I’m prioritizing your thread and will reply with concrete next steps as quickly as I can.`;
  }
  return `Is there anything else you’d like me to double-check while I have your account open?`;
}

/**
 * **Local / offline** generator used when `VITE_AI_WRITING_URL` is unset, or
 * as a safety fallback if the remote server returns an empty `suggestions` list.
 *
 * It uses **keyword heuristics** on the last user message + ticket metadata —
 * not an LLM — so responses are predictable and fast with no network call.
 *
 * @param {{ messages: Array<{ sender?: string, text?: string }>, draftText?: string, ticketSubject?: string, ticketSummary?: string }} input
 * @returns {string[]}
 */
export function generateLocalSuggestReplies(input) {
  const last = lastUserSnippet(input.messages);
  const ticket = (input.ticketSubject ?? "").trim();
  const lower = `${last} ${ticket} ${input.ticketSummary ?? ""}`.toLowerCase();

  const ack = last
    ? `Thanks for your message${last.length > 80 ? "" : ` — I’ve read what you shared about “${last.slice(0, 60)}${last.length > 60 ? "…" : ""}”.`}`
    : "Thanks for getting in touch.";

  const s1 =
    lower.includes("refund") || lower.includes("charge") || lower.includes("billing")
      ? `${ack} I’ve pulled up your billing profile and I’m reviewing the charges now. I’ll outline the next steps or any credits we can apply within this thread.`
      : lower.includes("password") || lower.includes("login") || lower.includes("sign in")
        ? `${ack} Let’s secure your account first. I’ll walk you through a reset and verify the email on file so you can sign in again without losing access.`
        : lower.includes("error") || lower.includes("404") || lower.includes("not working") || lower.includes("broken")
          ? `${ack} I’m checking this on our side against the timeline you gave. I’ll confirm whether it’s a known issue and share a workaround or ETA as soon as I have it.`
          : `${ack} I’m looking into this now and will keep everything in one place here so you don’t have to repeat yourself.`;

  const s2 =
    "I appreciate the detail — that helps. If anything changes on your end (new error text, screenshots, or timestamps), drop it here and I’ll fold it into the investigation.";

  const s3 = selectedTicketClosing(lower, ticket);

  return [s1, s2, s3].filter(Boolean);
}
